import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig, AxiosResponse } from 'axios';

// 토큰 저장 키 (auth.ts와 공유)
const TOKEN_KEY = 'spojoy_auth_token';
const REFRESH_TOKEN_KEY = 'spojoy_refresh_token';

// 환경변수가 설정되지 않았거나 빈 문자열이면 상대 경로 사용 (nginx proxy)
const API_URL = process.env.REACT_APP_API_URL !== undefined && process.env.REACT_APP_API_URL !== ''
  ? process.env.REACT_APP_API_URL
  : '';

// 토큰 갱신 중복 방지를 위한 플래그
let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

const onRefreshed = (token: string) => {
  refreshSubscribers.forEach(callback => callback(token));
  refreshSubscribers = [];
};

const addRefreshSubscriber = (callback: (token: string) => void) => {
  refreshSubscribers.push(callback);
};

// Axios 인스턴스 생성
const apiClient: AxiosInstance = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  withCredentials: true,
});

// 요청 인터셉터
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`[API 요청] ${config.method?.toUpperCase()} ${config.url}`);
    }

    if (!config.headers) {
      config.headers = {} as any;
    }

    if (config.method === 'get' && !config.url?.startsWith('/api/')) {
      config.params = {
        ...config.params,
        _t: Date.now()
      };
    }

    return config;
  },
  (error: AxiosError) => {
    console.error('[API 요청 오류]', error);
    return Promise.reject(error);
  }
);

// 강제 로그아웃 — auth.ts의 forceLogout과 동일 로직 (순환 import 방지용)
const doForceLogout = () => {
  console.log('[인증] 세션 만료로 로그아웃 처리');
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  localStorage.removeItem('spojoy_user');
  delete apiClient.defaults.headers.common['Authorization'];
  window.dispatchEvent(new CustomEvent('auth:session-expired'));
};

// 토큰 갱신 시도 (인터셉터 우회를 위해 raw axios 사용)
const tryRefreshToken = async (): Promise<string | null> => {
  const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY);
  if (!refreshToken) {
    return null;
  }

  try {
    const response = await axios.post(`${API_URL}/api/auth/refresh`, {
      refresh_token: refreshToken
    });

    const { access_token, refresh_token: newRefreshToken } = response.data;

    localStorage.setItem(TOKEN_KEY, access_token);
    localStorage.setItem(REFRESH_TOKEN_KEY, newRefreshToken);
    apiClient.defaults.headers.common['Authorization'] = `Bearer ${access_token}`;

    console.log('[인증] 토큰 갱신 성공');
    return access_token;
  } catch (error) {
    console.error('[인증] 토큰 갱신 실패');
    return null;
  }
};

// 응답 인터셉터
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    if (process.env.NODE_ENV === 'development') {
      console.log(`[API 응답] ${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status}`);
    }
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // CORS/네트워크 오류 처리
    if (!error.response && error.message === 'Network Error') {
      console.error('[네트워크 오류] API 서버가 다운되었거나 CORS 설정이 올바르지 않음.');
      if (process.env.NODE_ENV === 'development') {
        console.error('확인사항: 1) 백엔드 서버가 실행 중인지 확인:', API_URL);
        console.error('         2) SSL 인증서가 올바르게 설정되어 있는지 확인');
      }
    }

    // HTTP 상태 코드별 처리
    if (error.response) {
      const { status, data } = error.response as { status: number; data: any };
      const errorCode = data?.detail?.code;

      switch (status) {
        case 401:
          // 로그인/토큰 갱신 요청은 재시도 제외
          if (originalRequest.url?.includes('/auth/login') || originalRequest.url?.includes('/auth/refresh')) {
            break;
          }

          // 토큰 만료 시 갱신 시도
          if (errorCode === 'TOKEN_EXPIRED' && !originalRequest._retry) {
            originalRequest._retry = true;

            if (isRefreshing) {
              return new Promise((resolve) => {
                addRefreshSubscriber((token: string) => {
                  originalRequest.headers['Authorization'] = `Bearer ${token}`;
                  resolve(apiClient(originalRequest));
                });
              });
            }

            isRefreshing = true;
            const newToken = await tryRefreshToken();
            isRefreshing = false;

            if (newToken) {
              onRefreshed(newToken);
              originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
              return apiClient(originalRequest);
            }
          }

          // Refresh Token 만료/무효 또는 갱신 실패 시 로그아웃
          if (errorCode === 'REFRESH_TOKEN_EXPIRED' || errorCode === 'REFRESH_TOKEN_INVALID' || originalRequest._retry) {
            console.error('[인증 오류] Refresh Token 만료 - 재로그인 필요');
            doForceLogout();
            break;
          }

          // 기타 인증 오류
          console.error('[인증 오류] 인증이 필요하거나 토큰이 유효하지 않음');
          doForceLogout();
          break;

        case 403:
          console.error('[API 오류] 권한이 없음');
          break;
        case 404:
          console.error('[API 오류] 리소스를 찾을 수 없음');
          break;
        case 500:
          console.error('[API 오류] 서버 내부 오류', data);
          break;
        default:
          console.error(`[API 오류] 상태 코드: ${status}`, data);
      }
    } else if (error.request) {
      console.error('[API 오류] 응답 없음:', error.request);
    } else {
      console.error('[API 오류] 요청 설정 오류:', error.message);
    }

    return Promise.reject(error);
  }
);

export default apiClient;
